/**
 * Stops Management service.
 *
 * Implements the 9 endpoints listed in STOPS_MANAGEMENT_API_SPEC §5 against the
 * live BE controller (`vtf-stop-controller`, `/commute/rest/v3/stops`).
 *
 * The BE wire format diverges from the FE-canonical shape in three places —
 * labels (flat `labelEn`/`labelTr` instead of nested `label.{en,tr}`),
 * `coverage.polygonJson` (a stringified GeoJSON instead of an object), and the
 * `GET /stops` query (BE rejects `campusIds`/`includeUniversal`/`sortBy`).
 * All three deltas are normalised at the service boundary so consumers never
 * need to know about the wire format.
 */

import { stops as endpoints } from '@/endpoint.flexigo';
import Repository from './repository';

const client = new Repository();

// ----------------------------------------------------------------------------
// URL / query helpers
// ----------------------------------------------------------------------------

function fillUrl(template, params) {
  let url = template;
  Object.entries(params || {}).forEach(([key, value]) => {
    url = url.replace(`:${key}`, encodeURIComponent(value));
  });
  return url;
}

function paramsSerializer(params) {
  const search = new URLSearchParams();
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null || item === '') return;
        search.append(key, item);
      });
    } else {
      search.append(key, value);
    }
  });
  return search.toString();
}

/**
 * Strip query keys the BE doesn't accept (`campusIds`, `includeUniversal`,
 * `sortBy`) and drop empty arrays so we don't send `?categories=` etc.
 */
function pickAllowedListParams(query = {}) {
  const {
    q,
    categories,
    modules,
    onlyOnDemand,
    onlyAda,
    includeArchived,
    offset,
    limit,
    minLat,
    maxLat,
    minLng,
    maxLng,
  } = query;
  const params = {};
  if (q != null && q !== '') params.q = q;
  if (Array.isArray(categories) && categories.length > 0) params.categories = categories;
  if (Array.isArray(modules) && modules.length > 0) params.modules = modules;
  if (onlyOnDemand) params.onlyOnDemand = true;
  if (onlyAda) params.onlyAda = true;
  if (includeArchived) params.includeArchived = true;
  if (offset != null) params.offset = offset;
  if (limit != null) params.limit = limit;
  // VSER-13369: viewport bbox filter. The BE requires all four together (a partial
  // set is ignored server-side), so only attach them when every value is finite.
  if ([minLat, maxLat, minLng, maxLng].every((v) => typeof v === 'number' && Number.isFinite(v))) {
    params.minLat = minLat;
    params.maxLat = maxLat;
    params.minLng = minLng;
    params.maxLng = maxLng;
  }
  return params;
}

// ----------------------------------------------------------------------------
// Wire-format normalisers
// ----------------------------------------------------------------------------

/**
 * BE returns `{ key, labelEn, labelTr, pinColor, available }`.
 * FE consumers (legend, filter combo, edit panel, search) all read
 * `cat.label[lang]` — fold the flat fields back into a nested label.
 */
function normalizeCategory(raw) {
  if (!raw) return raw;
  const {
    labelEn, labelTr, label, ...rest
  } = raw;
  const en = label?.en ?? labelEn ?? '';
  const tr = label?.tr ?? labelTr ?? '';
  return { ...rest, label: { en, tr } };
}

/**
 * Same flat→nested fold for `StopSearchGroupDto`.
 */
function normalizeSearchGroup(raw) {
  if (!raw) return raw;
  const {
    labelEn, labelTr, label, ...rest
  } = raw;
  const en = label?.en ?? labelEn ?? '';
  const tr = label?.tr ?? labelTr ?? '';
  return { ...rest, label: { en, tr } };
}

/**
 * `StopCoverageDto.polygonJson` is a stringified GeoJSON on the wire. The map
 * + edit panel work with `coverage.polygon` as an object — parse on read.
 * Returns a new object so callers may safely mutate.
 */
function normalizeCoverageRead(coverage) {
  if (!coverage) return coverage;
  const { polygonJson, polygon, ...rest } = coverage;
  if (rest.type !== 'POLYGON') return { ...rest, polygon: null };
  if (polygon && typeof polygon === 'object') return { ...rest, polygon };
  if (typeof polygonJson === 'string' && polygonJson.length > 0) {
    try {
      return { ...rest, polygon: JSON.parse(polygonJson) };
    } catch (e) {
      // Malformed payload from BE — keep type so the UI can still render the
      // label, but no overlay until the data is fixed.
      return { ...rest, polygon: null };
    }
  }
  return { ...rest, polygon: null };
}

/**
 * Inverse of `normalizeCoverageRead` — serialises `polygon` (object) into
 * `polygonJson` (string) for the BE save payload.
 */
function normalizeCoverageWrite(coverage) {
  if (!coverage) return coverage;
  const { polygon, polygonJson, ...rest } = coverage;
  if (rest.type !== 'POLYGON') return { ...rest };
  if (typeof polygonJson === 'string' && polygonJson.length > 0) {
    return { ...rest, polygonJson };
  }
  if (polygon && typeof polygon === 'object') {
    return { ...rest, polygonJson: JSON.stringify(polygon) };
  }
  return { ...rest };
}

function normalizeStop(raw) {
  if (!raw) return raw;
  if (raw.coverage === undefined) return raw;
  return { ...raw, coverage: normalizeCoverageRead(raw.coverage) };
}

function normalizeSavePayload(payload = {}) {
  // On-demand stops carry no modules / category / coverage. The BE rejects a
  // stale RADIUS coverage object or an empty-string category with a 400, so
  // collapse them to the documented on-demand shape at the wire boundary
  // (create schema / spec §3.4: onDemand=true ⇒ modules:[], category:null,
  // coverage:null). The form keeps its internal coverage object so the map
  // still renders, but it must never reach the BE for an on-demand stop.
  if (payload.onDemand) {
    return {
      ...payload, modules: [], category: null, coverage: null,
    };
  }
  if (payload.coverage === undefined) return { ...payload };
  // A coverage object with no `type` means "no coverage" (Campus Stops,
  // VSER-14482: the campus polygon is the coverage and the BE backfill leaves
  // it null). The BE rejects `{ type: null }` with MISSING_DATA but accepts
  // `coverage: null` for CAMPUS_STOPS, so collapse it at the wire boundary.
  if (!payload.coverage || !payload.coverage.type) return { ...payload, coverage: null };
  return { ...payload, coverage: normalizeCoverageWrite(payload.coverage) };
}

// ----------------------------------------------------------------------------
// Public service surface
// ----------------------------------------------------------------------------

const service = {
  async getCategories() {
    const data = await client.get(endpoints.categories);
    return Array.isArray(data) ? data.map(normalizeCategory) : [];
  },

  /**
   * Authorized campuses for the Responsible Campus combo (VSER-14482):
   * `GET /v3/campuses?ownCampuses=true&allCampuses=true`. Returns the raw
   * `Campus` DTOs (`{ id, name, title, isCampus, parentCampusId, ... }`).
   */
  async getOwnCampuses() {
    const data = await client.get(endpoints.campuses);
    return Array.isArray(data) ? data : [];
  },

  async getList(query = {}) {
    const params = pickAllowedListParams(query);
    const { data } = await client.instance.get(endpoints.list, {
      params,
      paramsSerializer,
    });
    if (!data) return { items: [], offset: 0, limit: 0, total: 0 };
    return {
      ...data,
      items: Array.isArray(data.items) ? data.items.map(normalizeStop) : [],
    };
  },

  async search({ q, limit = 50 } = {}) {
    const { data } = await client.instance.get(endpoints.search, {
      params: { q, limit },
      paramsSerializer,
    });
    return Array.isArray(data) ? data.map(normalizeSearchGroup) : [];
  },

  async getStop(id) {
    const data = await client.get(fillUrl(endpoints.detail, { id }));
    return normalizeStop(data);
  },

  async getStopRoutes(id) {
    const data = await client.get(fillUrl(endpoints.routes, { id }));
    return data || { totalCount: 0, groups: [], ungrouped: [] };
  },

  async getDeleteCheck(id) {
    return client.get(fillUrl(endpoints.deleteCheck, { id }));
  },

  async createStop(payload) {
    const data = await client.post(endpoints.create, normalizeSavePayload(payload));
    return normalizeStop(data);
  },

  async updateStop(id, payload) {
    const data = await client.put(
      fillUrl(endpoints.update, { id }),
      normalizeSavePayload({ ...payload, id }),
    );
    return normalizeStop(data);
  },

  async deleteStop(id) {
    return client.delete(fillUrl(endpoints.delete, { id }));
  },
};

export default service;
